import type { CSSProperties } from "react";
import { cx } from "@/lib/cx";

const CANDLE_COUNT = 90;
const VIEWBOX_W = 1200;
const VIEWBOX_H = 560;
const PAD = 20;

interface Candle {
  open: number;
  close: number;
  high: number;
  low: number;
  bull: boolean;
}

function buildCandles(): {
  candles: Candle[];
  step: number;
  bodyW: number;
  yScale: (p: number) => number;
} {
  let seed = 42;
  const rng = (): number => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const out: Candle[] = [];
  let price = 125;
  const TREND = 180;
  for (let i = 0; i < CANDLE_COUNT; i++) {
    const phase = i / CANDLE_COUNT;
    const rally1 = 0.5 * (1 + Math.tanh((phase - 0.18) * 15)) * 105;
    const rally2 = 0.5 * (1 + Math.tanh((phase - 0.72) * 10)) * 100;
    const dip = -60 * Math.exp(-Math.pow((phase - 0.45) / 0.1, 2));
    const ripple = Math.sin(phase * Math.PI * 9) * 7;
    const target = TREND + rally1 + rally2 + dip + ripple;
    const spike = rng() < 0.12 ? 2.4 : 1;
    const noise = (rng() - 0.5) * 20 * spike;
    const change = (target - price) * 0.38 + noise;
    const open = price;
    const close = Math.max(40, price + change);
    const range = Math.abs(change);
    const wTop = rng() * (6 + range * 0.55);
    const wBot = rng() * (6 + range * 0.55);
    out.push({
      open,
      close,
      high: Math.max(open, close) + wTop,
      low: Math.min(open, close) - wBot,
      bull: close >= open,
    });
    price = close;
  }
  const minP = Math.min(...out.map((c) => c.low));
  const maxP = Math.max(...out.map((c) => c.high));
  const range = maxP - minP;
  const drawableH = VIEWBOX_H - PAD * 2;
  const drawableW = VIEWBOX_W - PAD * 2;
  const step = drawableW / out.length;
  return {
    candles: out,
    step,
    bodyW: step * 0.62,
    yScale: (p: number) => VIEWBOX_H - PAD - ((p - minP) / range) * drawableH,
  };
}

const DATA = buildCandles();

interface Props {
  opacity?: number;
  className?: string;
  style?: CSSProperties;
}

export default function CandlestickBackdrop({ opacity = 0.32, className, style }: Props): JSX.Element {
  const { candles, step, bodyW, yScale } = DATA;
  return (
    <div
      className={cx("cb-sweep absolute inset-0 pointer-events-none", className)}
      style={{ opacity, ...style }}
      aria-hidden="true"
    >
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        preserveAspectRatio="xMidYMid slice"
        style={{ width: "100%", height: "100%" }}
      >
        <defs>
          <filter id="cb-glow-filter" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>
        {candles.map((c, i) => {
          const x = PAD + i * step + step / 2;
          const yOpen = yScale(c.open);
          const yClose = yScale(c.close);
          const bodyTop = Math.min(yOpen, yClose);
          const bodyH = Math.max(2, Math.abs(yOpen - yClose));
          const fill = c.bull ? "rgb(var(--brand))" : "rgb(var(--fg-dim))";
          const glow = c.bull ? "rgb(var(--brand))" : "rgb(var(--fg))";
          const delay = (i / CANDLE_COUNT) * 6;
          return (
            <g key={i}>
              <line x1={x} x2={x} y1={yScale(c.high)} y2={yScale(c.low)} stroke="rgb(var(--border-strong))" strokeWidth="1.5" />
              <rect x={x - bodyW / 2} y={bodyTop} width={bodyW} height={bodyH} fill={fill} />
              <rect
                className="cb-glow"
                x={x - bodyW / 2 - 2}
                y={bodyTop - 2}
                width={bodyW + 4}
                height={bodyH + 4}
                fill={glow}
                filter="url(#cb-glow-filter)"
                style={{ animationDelay: `${delay}s` }}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}
